import {
  Box,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  InputGroup,
  InputRightAddon,
  ListItem,
  Spinner,
  UnorderedList,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  Currency,
  type PortalSourcingEventDetailDto,
  type PublicBidDto,
  type PublicBidPatchDto,
} from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { NumericInput } from "../../../../../common/input/NumericInput";

interface Props {
  bidId: string;
  event: PortalSourcingEventDetailDto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const PriceStep = ({ bidId, event, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<PublicBidDto, PublicBidPatchDto>(bidId);

  const currency = event.estimatedValue?.currency ?? Currency.Nok;
  const value = state.price?.value ?? null;
  const isSaving = fieldStatus("price") === "saving" || fieldStatus("price") === "pending";

  // value:null represents "no price" (deliver preflight blocks on it); the price object stays so currency is kept.
  const setValue = (next: number | null) =>
    updateField({ price: { value: next, currency } }, { debounceMs: 400 });

  return (
    <Box pb="8" pt="6">
      <Flex align="center" gap="2" mb="6">
        <Heading size="md">{t("Price")}</Heading>
        {isSaving && <Spinner size="sm" color="smMuted" />}
      </Flex>

      <Card variant="outline" rounded="xl" mb="8">
        <CardBody>
          <FormControl>
            <FormLabel fontSize="sm">{t("Total price")}</FormLabel>
            <InputGroup maxW="sm">
              <NumericInput
                numericValue={value}
                onNumericChange={(next) => setValue(next)}
                placeholder={t("Enter your total price") ?? ""}
              />
              <InputRightAddon>{currency}</InputRightAddon>
            </InputGroup>
            <FormHelperText>
              {t(
                "Enter your total price for the entire contract — covering the full contract period and all deliverables described in the procurement documents.",
              )}
            </FormHelperText>
          </FormControl>
        </CardBody>
      </Card>

      <UnorderedList spacing="1" fontSize="sm" color="smMuted" mb="6">
        <ListItem>
          {t("State the price in {{currency}}, excluding VAT, unless the procurement documents state otherwise.", {
            currency,
          })}
        </ListItem>
        <ListItem>
          {t("The price should be fixed for the whole contract and include all costs such as delivery, fees and expenses.")}
        </ListItem>
        <ListItem>{t("The buyer scores price automatically against the lowest delivered price.")}</ListItem>
      </UnorderedList>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
